import { useLanguage } from "@/context/LanguageContext";
import SectionLabel from "@/components/SectionLabel";
import {
  Heart,
  CalendarCheck,
  FileText,
  Search,
  Check,
} from "lucide-react";

const KIND_ICONS = [CalendarCheck, FileText, Search];

export default function Testimonials() {
  const { t } = useLanguage();
  const items = t("testimonials.items");

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative bg-[#FAFAF7] paper-grain border-t border-[#F0EBE0] overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-0 right-1/4 w-[420px] h-[420px] halo-soft opacity-30" />
      </div>

      <div className="container-iko relative py-24 lg:py-32">
        <div className="max-w-2xl mb-14 lg:mb-20">
          <SectionLabel icon={Heart}>{t("testimonials.eyebrow")}</SectionLabel>
          <h2 className="font-display font-semibold text-4xl lg:text-6xl leading-[1.02] tracking-[-0.034em] text-balance text-[#1A1814] mb-5">
            {t("testimonials.title")}
          </h2>
          <p className="text-base lg:text-lg text-[#6B6862] leading-relaxed max-w-xl">
            {t("testimonials.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {items.map((it, i) => {
            const Icon = KIND_ICONS[i % KIND_ICONS.length];
            return (
              <article
                key={i}
                data-testid={`usage-card-${i}`}
                className="group relative rounded-3xl bg-white border border-[#F0EBE0] p-6 lg:p-7 elev-card hover:elev-card-hover hover:-translate-y-1 transition-all duration-500 overflow-hidden flex flex-col"
              >
                <div className="absolute -top-12 -right-10 w-40 h-40 halo-soft opacity-0 group-hover:opacity-60 transition-opacity duration-700 pointer-events-none" />

                <div className="relative flex items-center justify-between mb-5">
                  <div className="inline-flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-[#F4F2EC] border border-[#F0EBE0] flex items-center justify-center text-[#437DEA]">
                      <Icon size={16} strokeWidth={1.8} />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] font-semibold text-[#1A1814]/55">
                      {it.when}
                    </span>
                  </div>
                </div>

                {/* User bubble */}
                <div className="relative flex justify-end mb-4">
                  <div className="max-w-[88%] px-3.5 py-2 bg-[#1A1814] text-white text-[13px] leading-snug rounded-2xl rounded-br-md">
                    {it.ask}
                  </div>
                </div>

                {/* Actions checklist */}
                <div className="relative space-y-2 mb-6">
                  {it.actions.map((a, j) => (
                    <div
                      key={j}
                      className="flex items-center gap-2.5 text-[13px] text-[#1A1814]"
                      style={{
                        animation: `fadeUp 0.5s ease-out ${j * 0.08}s both`,
                      }}
                    >
                      <div className="shrink-0 w-4 h-4 rounded-full bg-[#F4F2EC] border border-[#F0EBE0] group-hover:bg-[#437DEA] group-hover:border-[#437DEA] flex items-center justify-center transition-colors duration-500">
                        <Check
                          size={9}
                          strokeWidth={3}
                          className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        />
                      </div>
                      <span>{a}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto pt-4 border-t border-[#F0EBE0]/80">
                  <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#F4F2EC] border border-[#F0EBE0] text-[#437DEA] text-[12.5px] font-semibold group-hover:bg-[#437DEA] group-hover:border-[#437DEA] group-hover:text-white transition-all duration-500">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#437DEA] group-hover:bg-white transition-colors duration-500" />
                    {it.badge}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
